/**
 * Docs for `lib` module
 * @packageDocumentation
 */
import type Resources from "@folio-2026/resources"
import { AudioLoader } from "@folio-2026/resources"
import { EVENTS, Snap } from "@folio-2026/shared"
import EventEmitter from "@folio-2026/shared/eventemitter"
import type Lenis from "lenis"
import * as THREE from "three/webgpu"

import Debug, { debugAtom, debugStore } from "./lib/Debug"
import Pointer from "./lib/Pointer"
import Renderer from "./lib/Renderer"
import Sizes from "./lib/Sizes"
import Stats from "./lib/Stats"
import { useExperienceStore, useSectionStore } from "./lib/store"

export { debugAtom, debugStore, useExperienceStore, useSectionStore }

export const sections = ["loader", "works", "guestbook", "contact"] as const
export type Section = (typeof sections)[number]

/**
 * The Experience class is the main class for the experience.
 * It is a singleton class.
 */
export default class Experience extends EventEmitter {
  static instance: Experience | null = null // Singleton instance

  canvas: HTMLCanvasElement | undefined
  navigate: (path: string) => void = () => {}
  debug!: Debug
  sizes!: Sizes
  pointer!: Pointer
  scene!: THREE.Scene
  renderer!: Renderer
  lenis?: Lenis
  onUpdate: (params: { delta: number; elapsed: number }) => void = () => {}
  onResize: () => void = () => {}

  globalResources!: Resources["items"]

  stats!: Stats

  audioLoader!: AudioLoader
  volume: number = 1
  previousVolume: number = 1

  isMenuOpen: boolean = false

  clock!: THREE.Clock

  scrollInfo: {
    startingScroll: number
    actualScroll: number
    animatedScroll: number
    progress: number
    isScrolling: string | boolean
    snaps: Snap<Section>[] | null
    direction: -1 | 0 | 1
    currentSection: Snap<Section> | null
    nextSection: Snap<Section> | null
  }

  private constructor() {
    super()

    this.scrollInfo = {
      startingScroll: 0,
      actualScroll: 0,
      animatedScroll: 0,
      isScrolling: false,
      snaps: null,
      direction: 1,
      currentSection: null,
      nextSection: null,
      progress: 0,
    }
  }

  static getInstance(): Experience {
    if (!Experience.instance) {
      Experience.instance = new Experience()
    }
    return Experience.instance
  }

  async init() {
    console.log("%cExperience init", "color: #22c55e; font-weight: bold")

    this.debug = new Debug()
    this.sizes = new Sizes()
    this.pointer = new Pointer()

    this.clock = new THREE.Clock()

    this.scene = new THREE.Scene()

    this.renderer = new Renderer()
    this.stats = new Stats()

    // Initialize WebGPU renderer if needed
    if (
      this.renderer.instance &&
      "init" in this.renderer.instance &&
      typeof this.renderer.instance.init === "function"
    ) {
      await this.renderer.instance.init()
    }

    useExperienceStore.setState({ init: true })

    this.sizes.on("resize", this.resize.bind(this))
  }

  update() {
    const delta = this.clock.getDelta()
    const elapsed = this.clock.getElapsedTime()

    this.stats.update()

    this.updateVolume()

    this.onUpdate({ delta, elapsed })
  }

  private previousNormalizedVelocity: number = 0

  updateVolume() {
    const velocity = Math.abs(this.lenis?.velocity ?? 0)
    const normalizedVelocity =
      velocity < 50
        ? 0
        : velocity > 100
          ? 1
          : THREE.MathUtils.mapLinear(velocity, 50, 100, 0, 1)

    // Save volume when normalizedVelocity transitions from 0 to >0
    if (this.previousNormalizedVelocity === 0 && normalizedVelocity > 0) {
      this.previousVolume = this.volume
    }

    if (normalizedVelocity > 0) {
      // During fast scroll: volume decreases (1 - normalizedVelocity)
      // When normalizedVelocity = 1 (fastest): volume = 0
      // When normalizedVelocity approaches 0: volume approaches previousVolume
      this.volume = this.previousVolume * (1 - normalizedVelocity)
    } else {
      // When scroll stops: restore to previous volume
      this.volume = this.previousVolume
    }

    // Emit event when fast scroll ends (normalizedVelocity transitions from >0 to 0)
    if (this.previousNormalizedVelocity > 0 && normalizedVelocity === 0) {
      this.trigger(EVENTS.Audio.SetVolume, [{ volume: this.volume }])
    }

    this.previousNormalizedVelocity = normalizedVelocity
  }

  resize() {
    this.renderer.resize()
    this.onResize()
  }

  destroy() {
    // Remove listeners
    this.sizes.off("resize")
    this.pointer.destroy()

    // Traverse the whole scene and dispose
    this.scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()

        for (const key in child.material) {
          const value = child.material[key]
          if (typeof value?.dispose === "function") {
            value.dispose()
          }
        }
      }
    })

    // dispose camera
    // this.camera.controls.dispose();
    // dispose renderer
    this.renderer.instance.dispose()

    // dispose debug
    if (this.debug.active && this.debug.ui) {
      this.debug.ui.dispose()
    }
  }
}
